// Cricket squad flyer — Heraldic Premium (Reference-locked).
//
// Matches the product-owner target: luxury-navy portrait poster with
// a botanical-flourish topline, two-line Cinzel wordmark in metallic
// gold, Norman-heater shields flanking a laurel-wreath medallion, VS,
// three info tiles, ornamental "PLAYING SQUAD" divider, parchment
// ribbon player cards with dark ink names + green pills, red
// volunteer boxes with gold trim, and a bottom trophy + cricket-ball
// reminder.
//
// Wired to Rowdys Cricket Club + Pitt Gabruz Cricket via
// CricketFlyerV3 (FlyerCricket.jsx).

import React from "react";
import { CalendarDays, Clock, MapPin, Droplet, Banana, Swords } from "lucide-react";
import { SKILL_BY_CODE } from "../../data";
import Avatar from "../Avatar";
import {
  playerSkillCodes, teamLogoSrc,
  formatDateTemplate, formatTimeTemplate,
  sortPlayersForSport
} from "./flyerHelpers";

function fallbackInitials(name) {
  const parts = String(name || "").trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  const first = parts[0][0] || "";
  const last  = parts.length > 1 ? (parts[parts.length - 1][0] || "") : (parts[0][1] || "");
  return (first + last).toUpperCase();
}

// Shared metallic gold gradient — used inside every ornamental SVG so
// they colour-match the wordmark and shield bezels.
export function GoldDefs({ id }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0"    stopColor="#bf953f"/>
        <stop offset="0.25" stopColor="#fcf6ba"/>
        <stop offset="0.5"  stopColor="#b38728"/>
        <stop offset="0.75" stopColor="#fbf5b7"/>
        <stop offset="1"    stopColor="#aa771c"/>
      </linearGradient>
    </defs>
  );
}

// Vertical ornament used between info items — small stem with a
// diamond/hourglass ornament at the mid-point.
export function InfoDivider() {
  const gid = "fp-info-div";
  return (
    <svg
      className="fp-info-divider"
      viewBox="0 0 8 44"
      width="8"
      height="44"
      aria-hidden="true"
    >
      <GoldDefs id={gid}/>
      <g fill={`url(#${gid})`}>
        <rect x="3.4" y="0"  width="1.2" height="14"/>
        <path d="M 4 14 L 7.4 22 L 4 30 L 0.6 22 Z"/>
        <rect x="3.4" y="30" width="1.2" height="14"/>
      </g>
    </svg>
  );
}

// Baroque line flourish — horizontal filigree with diamond, botanical
// swash, and terminal dot. Mirror for the opposite side.
export function Flourish({ mirror = false, w = 100 }) {
  const gid = `fp-gold-grad-${mirror ? "l" : "r"}`;
  return (
    <svg
      className="fp-flourish"
      viewBox="0 0 100 14"
      width={w}
      height={w * 0.14}
      aria-hidden="true"
      style={mirror ? { transform: "scaleX(-1)" } : undefined}
    >
      <GoldDefs id={gid}/>
      <g fill={`url(#${gid})`}>
        {/* Horizontal filigree line */}
        <path d="M 0 6.4 H 52 L 54 5.6 L 55 7 L 54 8.4 L 52 7.6 H 0 Z"/>
        {/* Central diamond */}
        <path d="M 58 7 L 62 3 L 66 7 L 62 11 Z"/>
        {/* Botanical swash — teardrop with inner curl */}
        <path d="M 69 7
                 C 71 2, 79 2, 82 7
                 C 79 12, 71 12, 69 7 Z"/>
        <path
          d="M 74 5
             C 76 5, 78 6, 78 7
             C 78 8, 76 9, 74 9"
          fill="none"
          stroke="#0a1424"
          strokeWidth="0.6"
          strokeLinecap="round"
        />
        {/* Terminal dots */}
        <circle cx="88" cy="7" r="1.6"/>
        <circle cx="93" cy="7" r="0.9"/>
      </g>
    </svg>
  );
}

// Laurel-wreath medallion with 3 stars over a "PS" monogram.
function LaurelMedallion() {
  const gid = "fp-med-grad";
  return (
    <div className="fp-medallion">
      <div className="fp-medallion-inner">
        <svg
          viewBox="0 0 80 80"
          width="100%"
          height="100%"
          aria-hidden="true"
          className="fp-medallion-svg"
        >
          <GoldDefs id={gid}/>
          <g fill={`url(#${gid})`}>
            {/* Left laurel branch — 5 leaves curving from bottom-left up */}
            <g transform="translate(0,0)">
              <ellipse cx="16" cy="58" rx="4" ry="2.2" transform="rotate(-40 16 58)"/>
              <ellipse cx="12" cy="50" rx="4.2" ry="2.3" transform="rotate(-55 12 50)"/>
              <ellipse cx="10" cy="42" rx="4.4" ry="2.4" transform="rotate(-70 10 42)"/>
              <ellipse cx="11" cy="34" rx="4.2" ry="2.3" transform="rotate(-85 11 34)"/>
              <ellipse cx="15" cy="27" rx="3.8" ry="2.1" transform="rotate(-100 15 27)"/>
            </g>
            {/* Right laurel branch — mirror */}
            <g>
              <ellipse cx="64" cy="58" rx="4" ry="2.2" transform="rotate(40 64 58)"/>
              <ellipse cx="68" cy="50" rx="4.2" ry="2.3" transform="rotate(55 68 50)"/>
              <ellipse cx="70" cy="42" rx="4.4" ry="2.4" transform="rotate(70 70 42)"/>
              <ellipse cx="69" cy="34" rx="4.2" ry="2.3" transform="rotate(85 69 34)"/>
              <ellipse cx="65" cy="27" rx="3.8" ry="2.1" transform="rotate(100 65 27)"/>
            </g>
            {/* Bottom knot / bow */}
            <path d="M 34 62 L 40 66 L 46 62 L 44 64 L 46 66 L 40 68 L 34 66 L 36 64 Z"/>
            {/* 3 stars along the top */}
            {[
              { cx: 30, cy: 14, r: 2.8 },
              { cx: 40, cy: 11, r: 3.2 },
              { cx: 50, cy: 14, r: 2.8 },
            ].map((s, i) => (
              <path
                key={i}
                d={starPath(s.cx, s.cy, s.r)}
              />
            ))}
          </g>
          {/* PS monogram */}
          <text
            x="40" y="47"
            textAnchor="middle"
            fill={`url(#${gid})`}
            fontFamily='"Cinzel", Georgia, serif'
            fontWeight="900"
            fontSize="20"
            letterSpacing="1"
          >PS</text>
        </svg>
      </div>
    </div>
  );
}

function starPath(cx, cy, r) {
  const points = [];
  for (let i = 0; i < 10; i++) {
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.42;
    points.push(`${cx + Math.cos(angle) * rad},${cy + Math.sin(angle) * rad}`);
  }
  return `M ${points.join(" L ")} Z`;
}

// Cricket-ball + trophy composite for the reminder icon.
function TrophyBall() {
  const gid = "fp-trophy-grad";
  return (
    <svg
      viewBox="0 0 80 80"
      width="72"
      height="72"
      aria-hidden="true"
      className="fp-rem-trophy-svg"
    >
      <GoldDefs id={gid}/>
      <g>
        {/* Cricket ball — red sphere with white stitching */}
        <circle cx="18" cy="34" r="12" fill="#a52a2a"/>
        <circle cx="18" cy="34" r="12" fill="url(#fp-ball-shine)"/>
        <path d="M 8 32 Q 18 28 28 32" fill="none" stroke="#f4e8c8" strokeWidth="0.8" strokeDasharray="1.4 1"/>
        <path d="M 8 36 Q 18 40 28 36" fill="none" stroke="#f4e8c8" strokeWidth="0.8" strokeDasharray="1.4 1"/>
        <radialGradient id="fp-ball-shine" cx="0.3" cy="0.3" r="0.7">
          <stop offset="0"   stopColor="#ffffff" stopOpacity="0.28"/>
          <stop offset="0.6" stopColor="#ffffff" stopOpacity="0"/>
        </radialGradient>
        {/* Trophy */}
        <g fill={`url(#${gid})`}>
          {/* Cup */}
          <path d="M 40 20
                   L 68 20
                   L 66 42
                   Q 54 52, 42 42 Z"/>
          {/* Left handle */}
          <path d="M 40 22 Q 30 24, 32 34 Q 38 36, 42 32 Z"/>
          {/* Right handle */}
          <path d="M 68 22 Q 78 24, 76 34 Q 70 36, 66 32 Z"/>
          {/* Stem */}
          <rect x="52" y="48" width="4" height="10"/>
          {/* Base */}
          <rect x="44" y="58" width="20" height="4" rx="1"/>
          <rect x="42" y="62" width="24" height="6" rx="1.5"/>
        </g>
      </g>
    </svg>
  );
}

// Shield with rounded top corners + pointed bottom, via clip-path:
// path(). Bezel is metallic gold, interior is deep navy, logo image
// sits inside.
function TeamShield({ side, label, name, logoSrc, fallback }) {
  return (
    <div className={`fp-shield fp-shield--${side}`}>
      <div className="fp-shield-body">
        <div className="fp-shield-inner">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={name}
              crossOrigin="anonymous"
              onError={e => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <span className="fp-shield-fallback">{fallback}</span>
          )}
        </div>
      </div>
      <div className="fp-shield-nameplate">
        <b>{name}</b>
      </div>
    </div>
  );
}

function PlayerRibbon({ player, team, isCaptain, isKeeper }) {
  const codes = playerSkillCodes(team, player.id) || [];
  const seen = new Set();
  const pills = [];
  for (const code of codes) {
    if (seen.has(code)) continue;
    seen.add(code);
    const skill = SKILL_BY_CODE[code];
    if (!skill) continue;
    pills.push({ code, short: skill.short });
  }

  return (
    <div className="fp-ribbon-wrap">
      {/* Avatar sits OUTSIDE the clipped ribbon so it can overflow */}
      <div className="fp-ribbon-avatar-wrap">
        <div className="fp-ribbon-avatar">
          {player.id && player.avatarUpdatedAt ? (
            <Avatar
              member={{ id: player.id, name: player.name, avatarUpdatedAt: player.avatarUpdatedAt }}
              size={64}
              style={{ width: "100%", height: "100%", borderRadius: "50%", background: "transparent" }}
            />
          ) : (
            <span className="fp-ribbon-initials">{fallbackInitials(player.name)}</span>
          )}
        </div>
        {(isCaptain || isKeeper) && (
          <span className={`fp-ribbon-badge${isCaptain ? " fp-ribbon-badge--c" : " fp-ribbon-badge--wk"}`}>
            {isCaptain ? "C" : "WK"}
          </span>
        )}
      </div>
      <article className={`fp-ribbon${isCaptain ? " fp-ribbon--c" : ""}${isKeeper ? " fp-ribbon--wk" : ""}`}>
        <div className="fp-ribbon-name">{player.name}</div>
        <div className="fp-ribbon-pills">
          {pills.length === 0 ? (
            <span className="fp-ribbon-pill">Player</span>
          ) : (
            pills.map(p => (
              <span key={p.code} className="fp-ribbon-pill">{p.short}</span>
            ))
          )}
        </div>
      </article>
    </div>
  );
}

export function FlyerPoster({
  match, opp, homeTeamName,
  team, rules, startingPlayers, reportingTime,
  venueShort, venueAddress,
  showVolunteers, snacksVolunteer, alcoholVolunteer,
  captainPickId = null, wicketKeeperId = null,
  template = null
}) {
  const isRowdys = team?.code === "ROWDYS_CRICKET";
  const defaultLeagueLine = isRowdys
    ? "Pittsburgh Cricket League"
    : "Pittsburgh Premier Cricket League";
  const leagueLine = isRowdys
    ? defaultLeagueLine
    : (template?.leagueLine || defaultLeagueLine);

  const dateStr    = formatDateTemplate(match?.date) || "TBD";
  const reportStr  = formatTimeTemplate(reportingTime) || "TBD";
  const groundStr  = venueShort || "TBD";
  const awayName   = String(opp || "OPPONENT").toUpperCase();
  const homeName   = String(homeTeamName || "PITT GABRUZ").toUpperCase();
  const logoSrc    = teamLogoSrc(team);

  // Arrange by cricket lineup order (Opener → TopOrder → WK → AR → Middle
  // → Pace → Medium → LegSpin → OffSpin), ties break alphabetically.
  const roster = sortPlayersForSport(startingPlayers || [], team, "Cricket");

  return (
    <div className="poster fp fp-heraldic">

      <div className="fp-topline">
        <Flourish/>
        <span className="fp-topline-txt">{leagueLine}</span>
        <Flourish mirror/>
      </div>

      <h1 className="fp-brand">
        <span>GABRUZ</span>
        <span>UNIVERSE</span>
      </h1>
      <div className="fp-team-line">{homeName}</div>

      <div className="fp-info">
        <div className="fp-info-item">
          <span className="fp-info-icon"><Swords size={18}/></span>
          <div>
            <small>OPPONENT</small>
            <b>{awayName}</b>
          </div>
        </div>
        <InfoDivider/>
        <div className="fp-info-item">
          <span className="fp-info-icon"><CalendarDays size={18}/></span>
          <div>
            <small>MATCH DAY</small>
            <b>{dateStr}</b>
          </div>
        </div>
        <InfoDivider/>
        <div className="fp-info-item">
          <span className="fp-info-icon"><Clock size={18}/></span>
          <div>
            <small>REPORTING TIME</small>
            <b>{reportStr}</b>
          </div>
        </div>
        <InfoDivider/>
        <div className="fp-info-item">
          <span className="fp-info-icon"><MapPin size={18}/></span>
          <div>
            <small>GROUND</small>
            <b>{groundStr}</b>
          </div>
        </div>
      </div>

      <div className="fp-section-divider">
        <Flourish w={80}/>
        <span className="fp-section-divider-txt">Playing Squad</span>
        <Flourish w={80} mirror/>
      </div>

      <section className="fp-roster" aria-label="Playing squad">
        {roster.length === 0 && (
          <p className="fp-roster-empty">No players named yet.</p>
        )}
        {roster.map((p, i) => (
          <PlayerRibbon
            key={p.id || `p-${i}`}
            player={p}
            team={team}
            isCaptain={!!p.id && p.id === captainPickId}
            isKeeper={!!p.id && p.id === wicketKeeperId}
          />
        ))}
      </section>

      {showVolunteers && (
        <div className="fp-vol">
          <div className="fp-vol-item">
            <span className="fp-vol-item-icon"><Droplet size={22}/></span>
            <div className="fp-vol-item-body">
              <small>HYDRATION</small>
              <div className="fp-vol-item-role">WATER / GATORADE</div>
              <b>{snacksVolunteer || "TBD"}</b>
            </div>
          </div>
          <div className="fp-vol-item">
            <span className="fp-vol-item-icon"><Banana size={22}/></span>
            <div className="fp-vol-item-body">
              <small>NUTRITION</small>
              <div className="fp-vol-item-role">BANANAS / PROTEIN</div>
              <b>{alcoholVolunteer || "TBD"}</b>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
